import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LogoNormal from '../../assets/images/gt-logo-menu-normal.svg';
import LogoSmall from '../../assets/images/gt-logo-menu-small.svg';
import { SideMenu } from 'components/Custom/sideMenu';
import { UserMenu } from 'components/Custom/userMenu';
import { MenuSize } from 'store/app/enums';
import { setMenuSize } from 'store/app/actions';
import { useNavigate } from 'react-router-dom';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { useTranslation } from 'react-i18next';
import { getLocaleLayoutDirection } from 'i18n/locales';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { canUseAppSidebarWideLayout } from 'utils/responsive-panel-breakpoints';
import { AppSidePanel } from '@monorepo/shared-lib';

interface SidePanelProps {
  menuSize: MenuSize;
}

export const SidePanel: React.FC<SidePanelProps> = ({ menuSize }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentUser = useSelector(getAuthUser);
  const userLicence = useSelector(getAuthUserLicence);
  const { width: windowWidth } = useWindowDimensions();
  const isCompactScreen =
    windowWidth > 0 && !canUseAppSidebarWideLayout(windowWidth);

  const isWideMenu = menuSize === MenuSize.WIDE;
  const isRtl = getLocaleLayoutDirection(i18n.language) === 'rtl';
  const Logo = isWideMenu ? LogoNormal : LogoSmall;
  const dashboardURL = '/main';

  const handleRedirect = (url: string) => {
    if (url.includes('https://')) window.open(url, '_blank');
    else navigate(url);
  };

  const toggleMenuSize = () => {
    if (isCompactScreen) return;
    const newSize = isWideMenu ? MenuSize.NARROW : MenuSize.WIDE;
    dispatch(setMenuSize(newSize, currentUser?.userUuid));
  };

  return (
    <AppSidePanel
      menuSize={isWideMenu ? 'wide' : 'narrow'}
      logoSrc={Logo}
      logoHref={dashboardURL}
      logoAriaLabel={t('navigation.logo-alt')}
      logoWidthWide={142}
      logoWidthNarrow={53}
      canToggleMenu={!isCompactScreen}
      isRtl={isRtl}
      onToggleMenu={toggleMenuSize}
      hideMenuLabel={t('vat-report.hide-menu')}
      showMenuLabel={t('vat-report.show-menu')}
      footer={<UserMenu menuSize={menuSize} userLicence={userLicence} />}
    >
      <SideMenu
        currentUser={currentUser}
        redirectPage={handleRedirect}
        menuSize={menuSize}
        userLicence={userLicence}
        isNewReleaseSeen={currentUser?.isNewReleaseSeen ?? true}
      />
    </AppSidePanel>
  );
};
